import { Music } from 'types';
import { logger } from 'utils';
import { MUSIC, CONNECTOR, MOOD_CHILL, MOOD_POWER, SILENCE } from 'constants/audio';
import EventsManager from 'managers/events.manager';
import TRACKS from './tracks';
import calculateMood from './mood';
import { AudioManagerClass } from './../index';

const HISTORY_LENGTH = 5;
const FIRST_TRACK = 'LA_006';

class MusicManager {
  private base: AudioManagerClass;
  private play: (name: string) => void;
  private history: string[] = [];

  constructor(base: AudioManagerClass, play: (name: string) => void) {
    this.base = base;
    this.play = play;

    TRACKS[FIRST_TRACK].lazy = false;
    EventsManager.once('PLAY', () => {
      this.play(FIRST_TRACK);
      this.addToHistory(FIRST_TRACK);
    });
  }

  public onTrackFinished() {
    logger('music', 'trackFinished', this.current);
    if (this.current && this.current.silenceAfter) {
      window.setTimeout(this.playNext, this.current.silenceAfter * 1000);
      return;
    }

    this.playNext();
  }

  public playNext = () => {
    const next = this.getNext();
    logger('music', 'next track', next);
    this.play(next);
    this.addToHistory(next);
  }
  private getNext() {
    let next = this.calculateRandomNext();

    // simple prioritize tracks that are not in history
    // if track is in history get random again
    if (this.history.includes(next)) {
      next = this.calculateRandomNext();
    }

    return next;
  }
  private calculateRandomNext() {
    const mood = this.calculateMood();
    if (!this.current) return SILENCE;

    if (this.current.type === MUSIC && this.current.mood === MOOD_POWER) {
      // get random connector
      return this.randomConnector();
    } else if (this.current.mood === MOOD_CHILL) {
      // get random connector or track
      // prepare random connector, chill track and get random one from them
      const possibilities = [
        this.randomConnector(),
        this.randomTrack(MOOD_CHILL),
      ];
      return this.random(possibilities);
    } else if (this.current.type === CONNECTOR) {
      // get random track
      return this.randomTrack(mood);
    }

    return this.randomConnector();
  }
  private calculateMood() {
    return calculateMood();
  }

  private randomConnector() {
    const connectors = this.names.filter((name) =>
      this.getSound(name)!.type === CONNECTOR
      && !this.prevents.includes(name)
      && name !== this.latest,
    );

    return this.random(connectors);
  }
  private randomTrack(mood: number) {
    const tracks = this.names.filter((name) => {
      const track = this.getSound(name) as Music;
      if (!track) return false;

      return track.type === MUSIC &&
        track.mood === mood &&
        name !== this.latest &&
        !this.prevents.includes(name);
    });

    return this.random(tracks);
  }
  private random<T>(array: T[]): T {
    const index = Math.floor(Math.random() * array.length);
    return array[index];
  }

  private addToHistory(next: string) {
    this.history.unshift(next);
    if (this.history.length > HISTORY_LENGTH) {
      this.history.pop();
    }
  }

  private getSound(name: string) {
    return this.base.sounds.get(name);
  }

  get current() {
    return this.getSound(this.latest) as Music | null;
  }
  get prevents() {
    if (!this.current) return [];

    return this.current.prevents;
  }
  get sounds() {
    return TRACKS;
  }
  get names() {
    return Object.keys(this.sounds);
  }
  get latest() {
    if (!this.history.length) {
      return '';
    }

    return this.history[0];
  }
}

export default MusicManager;
